import type { ReactNode } from 'react';
import type { Tone } from '../lib/statusTones';

export type { Tone } from '../lib/statusTones';

// Stamped tags: colored text on the matching soft tint, 1.5px solid currentColor border.
const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-muted border-ink-muted/40',
  brand: 'bg-signal-soft text-signal border-signal/40',
  success: 'bg-success-soft text-success border-success/40',
  warning: 'bg-warning-soft text-warning border-warning/40',
  danger: 'bg-danger-soft text-danger border-danger/40',
  info: 'bg-info-soft text-info border-info/40',
};

const SIZE = {
  sm: 'px-1.5 py-px text-[0.5625rem]',
  md: 'px-2 py-0.5 text-[0.625rem]',
} as const;

/** Stamped status tag. DIN caps, 1.5px border, soft tint background. */
export function Badge({
  tone = 'neutral',
  size = 'md',
  dot = true,
  pulse = false,
  children,
  className = '',
}: {
  tone?: Tone;
  size?: keyof typeof SIZE;
  dot?: boolean;
  pulse?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-[3px] border-[1.5px] font-display font-bold uppercase tracking-[0.1em] ${SIZE[size]} ${TONE_CLASSES[tone]} ${className}`}
    >
      {dot && (
        <span
          className={`h-1.5 w-1.5 rounded-full bg-current ${pulse ? 'animate-pulse-soft' : ''}`}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}

/** The one "live right now" dot — signal orange, soft pulse. */
export function LiveDot({ className = 'h-1.5 w-1.5' }: { className?: string }) {
  return (
    <span
      className={`inline-block rounded-full bg-signal animate-pulse-soft ${className}`}
      aria-hidden="true"
    />
  );
}
